import { z } from 'zod';

/**
 * Audited events. The audit log is append-only; each row names one of these actions.
 * Adding an action is a code change reviewed like any other.
 */
export const AUDIT_ACTIONS = [
  'tenant.created',
  'tenant.suspended',
  'tenant.reactivated',
  'tenant.activated',
  'tenant.management_entered',
  'tenant.customer_number_changed',
  'tenant.customer_refreshed',
  'provisioning.started',
  'provisioning.retry_requested',
  'provisioning.failed',
  'store.opened',
  'store.updated',
  'auth.login_succeeded',
  'auth.login_failed',
  'auth.account_locked',
  'auth.logout',
  'auth.tenant_switched',
  'auth.password_changed',
  'auth.password_reset_requested',
  'auth.password_reset',
  'auth.password_set_by_platform',
  'auth.mfa_enrolled',
  'auth.mfa_verified',
  'auth.mfa_failed',
  'auth.mfa_recovery_code_used',
  'auth.mfa_recovery_codes_regenerated',
  'auth.mfa_reset',
  'auth.mfa_disabled',
  'session.revoked',
  'membership.role_granted',
  'membership.role_changed',
  'membership.revoked',
  'platform.role_granted',
  'platform.role_changed',
  'platform.role_revoked',
  'invitation.issued',
  'invitation.accepted',
  'catalog.ingest_requested',
  'catalog_sync.requested',
  'catalog_sync.completed',
  'catalog_sync.failed',
  'product.created',
  'product.updated',
  'category.created',
  'category.updated',
  'category.deleted',
  'product.media_changed',
  'product.logo_changed',
  'product.list_restored',
  'media.uploaded',
  'settings.updated',
  'theme.updated',
  'home.updated',
  'home_slide.created',
  'home_slide.updated',
  'home_slide.deleted',
  'content_page.created',
  'content_page.updated',
  'content_page.deleted',
  'shipping_zone.created',
  'shipping_zone.updated',
  'shipping_zone.deleted',
  'coupon.created',
  'coupon.updated',
  'coupon.deleted',
  'promotion.created',
  'promotion.updated',
  'promotion.deleted',
  'popup.created',
  'popup.updated',
  'popup.deleted',
  'order.status_changed',
  'order.quote_saved',
  'order.payment_changed',
  'customer.status_changed',
  'ai_copy.approved',
  'ai_copy.regeneration_requested',
  'ai_copy.retry_requested',
  'ai_copy.generation_requested',
  'ai_copy.settings_updated',
  'ai_copy.group_edited',
  'ai_copy.flyers_requested',
  'ai_budget.updated',
  'product_exclusions.updated',
  'catalog_exposure.updated',
  'domain.added',
  'domain.removed',
  'domain.check_requested',
  'domain.certificate_requested',
  'domain.primary_changed',
  'domain.ownership_verified',
  'domain.activated',
  'domain.failed',
  'domain.dns_lost',
  'domain.restored',
  'service_ticket.opened',
  'service_ticket.replied',
  'service_ticket.status_changed',
  'service_ticket.scan_requested',
] as const;
export const auditActionSchema = z.enum(AUDIT_ACTIONS);
export type AuditAction = z.infer<typeof auditActionSchema>;

export const AUDIT_ACTOR_KINDS = ['USER', 'ANONYMOUS', 'SYSTEM'] as const;
export const auditActorKindSchema = z.enum(AUDIT_ACTOR_KINDS);
export type AuditActorKind = z.infer<typeof auditActorKindSchema>;

/** Metadata holds safe, non-secret values only (never passwords, tokens or free-form input). */
export const auditMetadataSchema = z.record(
  z.string(),
  z.union([z.string(), z.number(), z.boolean(), z.null()]),
);
export type AuditMetadata = z.infer<typeof auditMetadataSchema>;

export const auditEntrySchema = z.object({
  id: z.uuid(),
  occurredAt: z.iso.datetime(),
  actorUserId: z.uuid().nullable(),
  actorKind: auditActorKindSchema,
  tenantId: z.uuid().nullable(),
  action: auditActionSchema,
  resourceType: z.string(),
  resourceId: z.string().nullable(),
  requestId: z.string().nullable(),
  ip: z.string().nullable(),
  metadata: auditMetadataSchema,
});
export type AuditEntry = z.infer<typeof auditEntrySchema>;

export const platformAuditQuerySchema = z.object({ tenantId: z.uuid().optional() });
